import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { CustomAxiosRequestConfig } from 'src/api/axiosClient/types';
import { JoinCampaignOption } from 'src/modules/campaigns/types';
import {
  CreateGroupRequestData,
  GetGroupsParams,
  Group,
  GroupDatePreviewParams,
  GroupDatePreviewResult,
  GroupStatus,
  TestGroupRulesRequestData,
  TestGroupRulesResult,
  UpdateGroupRequestData,
} from '../types';

export function useGroupsApi() {
  return useMemo(
    () => ({
      getGroups: (params: GetGroupsParams) => {
        return apiClient.get<{ items: Group[]; count: number }>(`/groups`, { params });
      },

      getGroup: (companyId: string, groupId: string) => {
        return apiClient.get<Group>(`/groups/${groupId}`, { params: { companyId } });
      },

      getDefaultGroup: async (companyId: string) => {
        const response = await apiClient.get<{ items: Group[]; count: number }>(`/groups`, {
          params: { companyId, types: 'default' },
        });

        return { ...response, data: response.data.items?.[0] };
      },

      getJoinCampaigns: (companyId: string, groupIds: string[]) => {
        return apiClient.get<JoinCampaignOption[]>(`/groups/join-campaigns`, {
          params: { companyId, groupIds: groupIds.join(',') },
        });
      },

      archiveUnarchiveGroup: (companyId: string, groupId: string, status: GroupStatus) => {
        return apiClient.patch<void>(`/groups/${groupId}`, { status }, { params: { companyId } });
      },

      createGroup: (companyId: string, data: CreateGroupRequestData) => {
        return apiClient.post<Group>(`/groups`, data, { params: { companyId } });
      },

      updateGroup: (companyId: string, groupId: string, data: UpdateGroupRequestData) => {
        return apiClient.put<void>(`/groups/${groupId}`, data, { params: { companyId } });
      },

      addContactsToGroup: (
        companyId: string,
        groupId: string,
        contactIds: string[],
        config?: CustomAxiosRequestConfig,
      ) => {
        return apiClient.post<void>(`/groups/${groupId}/add-contacts`, contactIds, {
          ...config,
          params: { companyId },
        });
      },

      removeContactsFromGroup: (companyId: string, groupId: string, contactIds: string[]) => {
        return apiClient.delete<void>(`/groups/${groupId}/remove-contacts`, {
          params: { companyId },
          data: contactIds,
        });
      },

      testGroupRules: (companyId: string, data: TestGroupRulesRequestData) => {
        return apiClient.post<TestGroupRulesResult>(`/groups/test-rules`, data, {
          params: { companyId },
        });
      },

      getGroupDatePreview: (companyId: string, params: GroupDatePreviewParams) => {
        return apiClient.get<GroupDatePreviewResult>(`/groups/date-preview`, {
          params: { companyId, ...params },
        });
      },
    }),
    [],
  );
}
